import { createContext, useContext } from 'react';

// 데이터 소스 타입
export enum DataSourceType {
  SENSOR = 'SENSOR',
  CCTV = 'CCTV',
  WEARABLE = 'WEARABLE',
  SYSTEM = 'SYSTEM'
}

// 데이터 우선순위
export enum DataPriority {
  LOW = 'LOW',
  MEDIUM = 'MEDIUM',
  HIGH = 'HIGH',
  CRITICAL = 'CRITICAL'
}

// 이벤트 타입
export enum EventType {
  DATA_UPDATE = 'DATA_UPDATE',
  THRESHOLD_EXCEEDED = 'THRESHOLD_EXCEEDED',
  ANOMALY_DETECTED = 'ANOMALY_DETECTED',
  DEVICE_STATUS = 'DEVICE_STATUS',
  SYSTEM_STATUS = 'SYSTEM_STATUS'
}

// 이벤트 인터페이스
export interface Event {
  id: string;
  timestamp: number;
  sourceType: DataSourceType;
  sourceId: string;
  eventType: EventType;
  priority: DataPriority;
  data: any;
  metadata?: Record<string, any>;
}

// 데이터 포인트 인터페이스
export interface DataPoint {
  id: string;
  sourceType: DataSourceType;
  sourceId: string;
  timestamp: number;
  value: number | string | boolean | object;
  unit?: string;
  metadata?: Record<string, any>;
}

// 이벤트 처리 콜백 함수 타입
export type EventHandler = (event: Event) => void;

// 데이터 통합 서비스 클래스
export class DataIntegrationService {
  private eventHandlers: Map<EventType, EventHandler[]> = new Map();
  private dataCache: Map<string, DataPoint> = new Map(); // sourceType:sourceId 키로 최신 데이터 저장
  private eventHistory: Event[] = [];
  private readonly maxEventHistory = 1000; // 최대 이벤트 히스토리 크기
  private readonly maxEventHistoryTimeWindow = 3600 * 24 * 1000; // 이벤트 히스토리 최대 시간 윈도우 (24시간)

  // 싱글톤 인스턴스
  private static instance: DataIntegrationService;

  // 생성자
  private constructor() {
    // 각 이벤트 타입에 대한 핸들러 배열 초기화
    Object.values(EventType).forEach(type => {
      this.eventHandlers.set(type, []);
    });
  }

  // 싱글톤 인스턴스 가져오기
  public static getInstance(): DataIntegrationService {
    if (!DataIntegrationService.instance) {
      DataIntegrationService.instance = new DataIntegrationService();
    }
    return DataIntegrationService.instance;
  }

  // 이벤트 핸들러 등록
  public addEventListener(eventType: EventType, handler: EventHandler): () => void {
    const handlers = this.eventHandlers.get(eventType) || [];
    handlers.push(handler);
    this.eventHandlers.set(eventType, handlers);

    // 구독 취소 함수 반환
    return () => {
      const currentHandlers = this.eventHandlers.get(eventType) || [];
      this.eventHandlers.set(
        eventType,
        currentHandlers.filter(h => h !== handler)
      );
    };
  }

  // 모든 이벤트 타입에 대한 핸들러 등록
  public addEventListenerForAll(handler: EventHandler): () => void {
    const unsubscribers: Array<() => void> = [];
    
    Object.values(EventType).forEach(type => {
      unsubscribers.push(this.addEventListener(type, handler));
    });

    // 모든 구독 취소 함수를 하나로 결합하여 반환
    return () => {
      unsubscribers.forEach(unsubscribe => unsubscribe());
    };
  }

  // 이벤트 발생 처리
  public processEvent(event: Event): void {
    // 이벤트 히스토리에 추가
    this.addToEventHistory(event);
    
    // 이벤트 타입에 맞는 핸들러 호출
    const handlers = this.eventHandlers.get(event.eventType) || [];
    handlers.forEach(handler => {
      try {
        handler(event);
      } catch (error) {
        console.error(`Error in event handler for ${event.eventType}:`, error);
      }
    });

    // 데이터 업데이트인 경우 캐시 갱신
    if (event.eventType === EventType.DATA_UPDATE && 'value' in event.data) {
      const dataPoint: DataPoint = {
        id: `${event.sourceType}:${event.sourceId}:${event.timestamp}`,
        sourceType: event.sourceType,
        sourceId: event.sourceId,
        timestamp: event.timestamp,
        value: event.data.value,
        unit: event.data.unit,
        metadata: event.metadata
      };
      this.updateDataCache(dataPoint);
    }
  }

  // 데이터 캐시 업데이트
  private updateDataCache(dataPoint: DataPoint): void {
    const key = `${dataPoint.sourceType}:${dataPoint.sourceId}`;
    this.dataCache.set(key, dataPoint);
  }

  // 이벤트 히스토리에 추가
  private addToEventHistory(event: Event): void {
    this.eventHistory.push(event);
    
    // 이벤트 히스토리 크기 제한 관리
    this.pruneEventHistory();
  }

  // 이벤트 히스토리 정리 (최대 크기 및 시간 윈도우 유지)
  private pruneEventHistory(): void {
    // 최대 크기 초과 시 가장 오래된 이벤트 제거
    if (this.eventHistory.length > this.maxEventHistory) {
      this.eventHistory = this.eventHistory.slice(-this.maxEventHistory);
    }
    
    // 지정된 시간 윈도우보다 오래된 이벤트 제거
    const cutoffTime = Date.now() - this.maxEventHistoryTimeWindow;
    this.eventHistory = this.eventHistory.filter(event => event.timestamp >= cutoffTime);
  }

  // 최신 데이터 포인트 가져오기
  public getLatestDataPoint(sourceType: DataSourceType, sourceId: string): DataPoint | null {
    const key = `${sourceType}:${sourceId}`;
    return this.dataCache.get(key) || null;
  }

  // 이벤트 히스토리 가져오기
  public getEventHistory(
    options?: {
      sourceType?: DataSourceType;
      sourceId?: string;
      eventType?: EventType;
      startTime?: number;
      endTime?: number;
      limit?: number;
    }
  ): Event[] {
    let filteredEvents = [...this.eventHistory];

    // 필터 적용
    if (options) {
      if (options.sourceType) {
        filteredEvents = filteredEvents.filter(e => e.sourceType === options.sourceType);
      }
      
      if (options.sourceId) {
        filteredEvents = filteredEvents.filter(e => e.sourceId === options.sourceId);
      }
      
      if (options.eventType) {
        filteredEvents = filteredEvents.filter(e => e.eventType === options.eventType);
      }
      
      if (options.startTime !== undefined) {
        filteredEvents = filteredEvents.filter(e => e.timestamp >= options.startTime!);
      }
      
      if (options.endTime !== undefined) {
        filteredEvents = filteredEvents.filter(e => e.timestamp <= options.endTime!);
      }
      
      if (options.limit && options.limit > 0) {
        filteredEvents = filteredEvents.slice(-options.limit);
      }
    }

    // 시간 순서로 정렬하여 반환
    return filteredEvents.sort((a, b) => a.timestamp - b.timestamp);
  }

  // 모든 데이터 소스 목록 가져오기
  public getAllDataSources(): { sourceType: DataSourceType; sourceId: string }[] {
    const sources = Array.from(this.dataCache.keys()).map(key => {
      const [sourceType, sourceId] = key.split(':');
      return {
        sourceType: sourceType as DataSourceType,
        sourceId
      };
    });
    
    return sources;
  }

  // 소스 타입별 데이터 소스 목록 가져오기
  public getDataSourcesByType(sourceType: DataSourceType): string[] {
    return this.getAllDataSources()
      .filter(source => source.sourceType === sourceType)
      .map(source => source.sourceId);
  }

  // 데이터 구독 생성 (새 데이터 발생 시 콜백 호출)
  public subscribeToDataSource(
    sourceType: DataSourceType,
    sourceId: string,
    callback: (dataPoint: DataPoint) => void
  ): () => void {
    const handler = (event: Event) => {
      if (
        event.sourceType === sourceType &&
        event.sourceId === sourceId &&
        event.eventType === EventType.DATA_UPDATE &&
        'value' in event.data
      ) {
        const dataPoint: DataPoint = {
          id: `${event.sourceType}:${event.sourceId}:${event.timestamp}`,
          sourceType: event.sourceType,
          sourceId: event.sourceId,
          timestamp: event.timestamp,
          value: event.data.value,
          unit: event.data.unit,
          metadata: event.metadata
        };
        callback(dataPoint);
      }
    };

    // 구독 등록
    return this.addEventListener(EventType.DATA_UPDATE, handler);
  }

  // 이벤트 생성 헬퍼 메소드
  public static createEvent(
    sourceType: DataSourceType,
    sourceId: string,
    eventType: EventType,
    data: any,
    priority: DataPriority = DataPriority.MEDIUM,
    metadata?: Record<string, any>
  ): Event {
    return {
      id: `${sourceType}:${sourceId}:${Date.now()}:${Math.random().toString(36).substring(2, 10)}`,
      timestamp: Date.now(),
      sourceType,
      sourceId,
      eventType,
      priority,
      data,
      metadata
    };
  }

  // 임계값 초과 이벤트 생성 헬퍼
  public static createThresholdEvent(
    sourceType: DataSourceType,
    sourceId: string,
    value: number,
    threshold: number,
    unit: string,
    isHighThreshold: boolean,
    priority: DataPriority = DataPriority.HIGH,
    metadata?: Record<string, any>
  ): Event {
    return DataIntegrationService.createEvent(
      sourceType,
      sourceId,
      EventType.THRESHOLD_EXCEEDED,
      {
        value,
        threshold,
        unit,
        direction: isHighThreshold ? 'high' : 'low'
      },
      priority,
      metadata
    );
  }

  // 이상 감지 이벤트 생성 헬퍼
  public static createAnomalyEvent(
    sourceType: DataSourceType,
    sourceId: string,
    value: number,
    expectedRange: [number, number],
    unit: string,
    anomalyScore: number,
    priority: DataPriority = DataPriority.HIGH,
    metadata?: Record<string, any>
  ): Event {
    return DataIntegrationService.createEvent(
      sourceType,
      sourceId,
      EventType.ANOMALY_DETECTED,
      {
        value,
        expectedRange,
        unit,
        anomalyScore
      },
      priority,
      metadata
    );
  }

  // 장치 상태 이벤트 생성 헬퍼
  public static createDeviceStatusEvent(
    sourceType: DataSourceType,
    sourceId: string,
    status: 'online' | 'offline' | 'error' | 'warning',
    message: string,
    priority: DataPriority = DataPriority.MEDIUM,
    metadata?: Record<string, any>
  ): Event {
    return DataIntegrationService.createEvent(
      sourceType,
      sourceId,
      EventType.DEVICE_STATUS,
      {
        status,
        message
      },
      priority,
      metadata
    );
  }

  // 실시간 데이터 업데이트 이벤트 생성 헬퍼
  public static createDataUpdateEvent(
    sourceType: DataSourceType,
    sourceId: string,
    value: number | string | boolean | object,
    unit?: string,
    metadata?: Record<string, any>
  ): Event {
    return DataIntegrationService.createEvent(
      sourceType,
      sourceId,
      EventType.DATA_UPDATE,
      {
        value,
        unit
      },
      DataPriority.LOW,
      metadata
    );
  }
}

// React Context 생성
interface DataIntegrationContextProps {
  service: DataIntegrationService;
}

const DataIntegrationContext = createContext<DataIntegrationContextProps | undefined>(undefined);

// 서비스 인스턴스 추출을 위한 커스텀 훅
export const useDataIntegration = () => {
  const context = useContext(DataIntegrationContext);
  if (!context) {
    throw new Error('useDataIntegration must be used within a DataIntegrationProvider');
  }
  return context.service;
};

// 싱글톤 서비스 인스턴스 생성
const dataIntegrationService = DataIntegrationService.getInstance();

export default dataIntegrationService; 